import React, { useState } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { homePathForRole } from "../lib/dashboardRole";
import { extractError } from "../lib/api";
import BrandLogo from "../components/BrandLogo";
import { DEMO_ACCOUNTS, IS_DEMO } from "../lib/demo";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Loader2 } from "lucide-react";

export default function LoginPage() {
  const { user, login, loading } = useAuth();
  const [email, setEmail] = useState("admin@bansal.com");
  const [password, setPassword] = useState("admin123");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  if (loading) return null;
  if (user) return <Navigate to={homePathForRole(user.role)} replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      await login(email, password);
    } catch (err) {
      setError(extractError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-[100dvh] flex flex-col lg:grid lg:grid-cols-2 lg:bg-transparent">
      {/* Mobile — dark hero matches desktop left panel */}
      <div className="lg:hidden gradient-hero flex flex-col items-center px-6 pt-[max(2.75rem,env(safe-area-inset-top))] pb-10 text-bansal-ivory text-center">
        <BrandLogo variant="onDarkCenter" data-testid="login-logo-mobile" />
        <p className="text-sm text-bansal-ivory/65 mt-6 max-w-xs leading-relaxed">
          Rice mill operations on the plant floor
        </p>
      </div>

      {/* Desktop hero */}
      <div className="hidden lg:flex relative gradient-hero flex-col justify-between p-12 text-bansal-ivory">
        <BrandLogo variant="onDark" data-testid="login-logo" />

        <div className="space-y-6 animate-fade-up">
          <h1 className="font-serif text-5xl xl:text-6xl leading-[1.05] tracking-tight">
            Every grain,<br />
            <span className="text-bansal-gold italic">accounted for.</span>
          </h1>
          <p className="text-bansal-ivory/75 max-w-md text-sm leading-relaxed">
            The operating system for premium Basmati rice — from gate security
            and contract negotiation to final payout. Built for mill owners,
            trusted by the floor.
          </p>
          <div className="grid grid-cols-3 gap-6 pt-4 max-w-md">
            {[
              { k: "98.6%", v: "Weighbridge accuracy" },
              { k: "11", v: "Modules integrated" },
              { k: "24×7", v: "Live dashboard" },
            ].map((s) => (
              <div key={s.v}>
                <div className="font-serif text-3xl text-bansal-gold">{s.k}</div>
                <div className="text-[11px] uppercase tracking-widest text-bansal-ivory/60 mt-1">{s.v}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="text-[11px] tracking-widest uppercase text-bansal-ivory/40">
          v1 · private build · bansal cereal products
        </div>
      </div>

      {/* Form — cream panel; rounded top on mobile over hero */}
      <div className="flex-1 flex items-start lg:items-center justify-center px-4 pb-[max(1.5rem,env(safe-area-inset-bottom))] lg:p-8 bg-muted rounded-t-[1.75rem] lg:rounded-none -mt-5 lg:mt-0 relative z-10 shadow-[0_-8px_30px_rgba(0,0,0,0.12)] lg:shadow-none">
        <div className="w-full max-w-md py-2 lg:py-0">
          <div className="rounded-2xl bg-card shadow-xl border border-white/20 p-5 sm:p-6 lg:rounded-none lg:bg-transparent lg:shadow-none lg:border-0 lg:p-0">
          <div className="hidden lg:block mb-8">
            <div className="flex items-center gap-2 text-[10px] tracking-[0.34em] uppercase text-primary font-medium mb-3">
              <span className="inline-block h-px w-6 bg-bansal-gold" />
              Mill Operator Portal
            </div>
            <h2 className="font-serif text-4xl xl:text-[2.6rem] mb-2 tracking-tight leading-[1.05]">Sign in</h2>
            <p className="text-sm text-muted-foreground">
              Enter your credentials to access the command center.
            </p>
          </div>

          <div className="lg:hidden mb-6">
            <h2 className="font-serif text-xl text-foreground">Sign in</h2>
            <p className="text-sm text-muted-foreground mt-1">Use your mill operator credentials</p>
          </div>

          <form onSubmit={onSubmit} className="space-y-4" data-testid="login-form">
            <div>
              <Label htmlFor="email" className="text-xs uppercase tracking-widest text-muted-foreground">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="mt-2 h-11"
                data-testid="login-email-input"
              />
            </div>

            <div>
              <Label htmlFor="password" className="text-xs uppercase tracking-widest text-muted-foreground">Password</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="mt-2 h-11"
                data-testid="login-password-input"
              />
            </div>

            {error && (
              <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2" data-testid="login-error">
                {error}
              </div>
            )}

            <Button
              type="submit"
              disabled={submitting}
              className="w-full h-12 rounded-xl bg-bansal-sage hover:bg-bansal-sageDark text-white touch-manipulation"
              data-testid="login-submit-button"
            >
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Enter the Mill"}
            </Button>
          </form>

          {IS_DEMO && (
            <div className="mt-8 border-t border-dashed pt-4 text-xs text-muted-foreground" data-testid="demo-accounts-panel">
              <div className="uppercase tracking-widest text-[10px] text-amber-800 mb-2 font-medium">
                Training accounts — safe to practice
              </div>
              <div className="space-y-1 max-h-48 overflow-y-auto pr-1">
                {DEMO_ACCOUNTS.map((a) => (
                  <button
                    key={a.email}
                    type="button"
                    className="block w-full text-left rounded-md px-2 py-1.5 hover:bg-amber-50 border border-transparent hover:border-amber-200 transition-colors"
                    onClick={() => {
                      setEmail(a.email);
                      setPassword(a.password);
                    }}
                  >
                    <span className="text-[10px] uppercase tracking-wider text-primary">{a.role}</span>
                    <div className="font-mono text-[11px] text-foreground">{a.email} / {a.password}</div>
                  </button>
                ))}
              </div>
            </div>
          )}
          </div>
        </div>
      </div>
    </div>
  );
}
